import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  DestroyRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ClockIconComponent, WarningIconComponent } from '@kuiper/icons';
import {
  CREATED_BY_ME_ARCHIVE_ACTION,
  CREATED_BY_ME_FILTER_OPTIONS,
  CREATED_BY_ME_INSIGHTS,
  CREATED_BY_ME_RESTORE_ACTION,
  CREATED_BY_ME_VIEW_DETAILS_ACTION,
  CREATED_BY_ME_ON_HOLD_TABLE_FILTER_TABS,
  CREATED_BY_ME_ON_HOLD_COLUMNS,
  filterMapping,
} from './created-by-me.constants';
import { COLUMN_IDENTIFIERS } from '../../const';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { PermissionsDirective } from '../../../../../ui-components/directives/permissions/permissions.directive';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { ExportService } from '../../../../../ui-components/services/export/export.service';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { MyInboxService } from '../../../../common/services/my-inbox/my-inbox.service';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { PermissionsService } from '../../../../common/services/permissions/permissions.service';
import { ArchiveConfirmationPopupComponent } from '../../../../common/components/archive-confirmation-popup/archive-confirmation-popup.component';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { Insight } from '../../../../common/models/insight.model';
import { AppPermissions } from '../../../../common/constants/app-permissions';
import { ActionList } from '../../../../common/models/action-list.model';
import { Permission } from '../../../../common/models/permission.model';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../common/constants/pagination.constants';
import { SlaDirective } from '../../../../common/directives/sla/sla.directive';
import { CreatedRequest } from '../../../../common/models/created-by-me.model';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import {
  ApiResponse,
  FilteredTableComponent,
  FilteredTablePayload,
  Meta,
} from '../../../../common';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CREATED_TASKS_ADAPTER } from './created-tasks.adapter';
import { EmployeeCreatedTasksAdapter } from './employee-created-tasks.adapter';

@Component({
  selector: 'lib-created-by-me',
  imports: [
    AvatarComponent,
    DatePipe,
    BadgeComponent,
    StepStatusPipe,
    FilterTableColByFeatFlagPipe,
    SlaDirective,
    TranslocoPipe,
    TruncatePipe,
    UserDropdownViewComponent,
    ActionListComponent,
    PermissionsDirective,
    TrimTooltipDirective,
    RouterLink,
    WarningIconComponent,
    ClockIconComponent,
    ArchiveConfirmationPopupComponent,
    RouterLink,
    TenantPathPipe,
    FilteredTableComponent,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './created-by-me.component.html',
  styleUrl: './created-by-me.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CreatedByMeComponent implements OnInit {
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly RequestTypeEnum = RequestTypeEnum;
  readonly appPermissions = AppPermissions;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;
  protected readonly filterMapping = filterMapping;

  private readonly createdTasksAdapter =
    inject(CREATED_TASKS_ADAPTER, { optional: true, skipSelf: true }) ??
    inject(EmployeeCreatedTasksAdapter);
  private readonly modalService = inject(ModalServiceService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly toasterService = inject(ToasterService);
  private readonly myInboxService = inject(MyInboxService);
  private readonly transloco = inject(TranslocoService);
  protected readonly parallelStepStatus = StepStatusEnum;
  private readonly router = inject(Router);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly exportService = inject(ExportService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly showProjectLink = signal(
    this.createdTasksAdapter.uiFeatures.showProjectLink
  );

  protected readonly showParentTasksColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.INBOX.parent_wf_column
    )
  );

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /******** pagination ********/
  meta = signal<Meta | undefined>(undefined);

  /*********** modal *********/
  archiveModal = viewChild<TemplateRef<unknown>>('archive');
  restoreModal = viewChild<TemplateRef<unknown>>('restore');
  selectedWorkflowId = signal<number>(0);

  /*********** table  *********/
  insights = signal<Insight[]>(CREATED_BY_ME_INSIGHTS);
  isLoading = signal<boolean>(false);
  dataSource = signal<CreatedRequest[]>([]);
  readonly columns = computed<TableColumns[]>(() =>
    CREATED_BY_ME_ON_HOLD_COLUMNS.filter(col =>
      isFeatureFlaggedColumnVisible(col.header, {
        showParentTasksColumn: this.showParentTasksColumn(),
        showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
      })
    ).map(column => {
      return {
        ...column,
        header: this.transloco.translate(column.header),
        showViewIcon: column.showViewIcon
          ? (row: CreatedRequest) =>
              (
                column.showViewIcon as (
                  r: CreatedRequest,
                  p: PermissionsService,
                  a: typeof AppPermissions
                ) => boolean
              )(row, this.permissionsService, this.appPermissions)
          : undefined,
        viewIconRouterLink: column.showViewIcon
          ? (row: CreatedRequest) => [
              this.createdTasksAdapter.getRequestViewLink(row.id),
            ]
          : undefined,
      };
    })
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    CREATED_BY_ME_ON_HOLD_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: 25,
    search: '',
    sortColumn: {
      key: '',
      value: 'asc',
    },
    filters: {},
    tab: this.tableFilterTabs()[0],
  });
  activeTableFilterTab = computed(
    () => this.payload().tab || this.tableFilterTabs()[0]
  );

  viewDetailsAction: ActionList = {
    ...CREATED_BY_ME_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(CREATED_BY_ME_VIEW_DETAILS_ACTION.label),
  };

  archiveAction: ActionList = {
    ...CREATED_BY_ME_ARCHIVE_ACTION,
    label: this.transloco.translate(CREATED_BY_ME_ARCHIVE_ACTION.label),
  };

  restoreAction: ActionList = {
    ...CREATED_BY_ME_RESTORE_ACTION,
    label: this.transloco.translate(CREATED_BY_ME_RESTORE_ACTION.label),
  };

  counterRefresh = output<string>();
  actionSourceTabName = input<string>();

  filterOptions: FilterOption[] = CREATED_BY_ME_FILTER_OPTIONS.filter(
    option =>
      this.createdTasksAdapter.uiFeatures.showResourceTypeFilter ||
      option.key !== 'resourceType'
  ).map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  ngOnInit(): void {
    this.getInsights();
  }

  getInsights() {
    const keys = [
      StepStatusEnum.INPROGRESS,
      StepStatusEnum.COMPLETED,
      StepStatusEnum.REJECTED,
      StepStatusEnum.ON_HOLD,
    ];
    this.createdTasksAdapter
      .getInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: { data: Insight[] }) => {
          keys.forEach((key, index) => {
            this.insights()[index].value =
              res.data
                .find(
                  (insight: Insight) =>
                    insight.key === key ||
                    (key === StepStatusEnum.INPROGRESS &&
                      insight.key === 'inprogress')
                )
                ?.value?.toString() ?? '0';
          });
          this.insights.set([...this.insights()]);
        },
      });
  }

  checkCalledApi() {
    this.checkReturnedObservable().subscribe({
      next: (res: ApiResponse<CreatedRequest>) =>
        this.handleRequestsSuccess(res),
    });
  }

  checkReturnedObservable = () => {
    return (
      this.activeTableFilterTab().key === 'archived'
        ? this.getArchiveRequests$()
        : this.getRequests$()
    ).pipe(
      finalize(() => this.isLoading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    );
  };

  getRequests$() {
    this.isLoading.set(true);
    this.meta.set(undefined);
    const { page, size, tab, sortColumn, search, filters } = this.payload();
    return this.createdTasksAdapter.getTasks(
      page,
      size,
      tab?.key ?? 'all',
      sortColumn,
      search,
      filters
    );
  }

  getArchiveRequests$() {
    this.isLoading.set(true);
    this.meta.set(undefined);
    const { page, size, sortColumn, search, filters } = this.payload();

    return this.createdTasksAdapter.getArchiveTasks(
      page,
      size,
      sortColumn,
      search,
      filters
    );
  }

  handleRequestsSuccess(res: ApiResponse<CreatedRequest>) {
    const data = res.data.map((item: CreatedRequest) => ({
      ...item,
      assignees: item.current_assignees.map(assignee => assignee.data),
      assigneeType: item.current_assignees.map(assignee => assignee.type),
    }));
    this.dataSource.set(data);
    this.meta.set(res.meta);
  }

  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.createdTasksAdapter.getRelatedRequestViewLink(childFlow.id);

  viewRequest(id: number) {
    this.router.navigate([this.createdTasksAdapter.getRequestViewLink(id)]);
  }

  getStatusActionList(
    status: string,
    permissions: Permission[] | undefined,
    requestId: number
  ) {
    const viewDetailsActionWithId: ActionList = {
      ...this.viewDetailsAction,
      routerLinks: [this.createdTasksAdapter.getRequestViewLink(requestId)],
    };

    if (
      (status === 'completed' || status === 'rejected') &&
      this.permissionsService.checkPermission(
        [this.appPermissions.workflow.archive],
        permissions
      )
    ) {
      return [
        viewDetailsActionWithId,
        this.activeTableFilterTab().key === 'archived'
          ? this.restoreAction
          : this.archiveAction,
      ];
    } else if (
      this.permissionsService.checkPermission(
        [this.appPermissions.workflow.view],
        permissions
      )
    ) {
      return [viewDetailsActionWithId];
    }
    return [];
  }

  actionListItemClick(option: string, workflow: CreatedRequest) {
    this.selectedWorkflowId.set(workflow.id);
    switch (option) {
      case 'archive':
        this.openModal('archive');
        break;

      case 'restore':
        this.openModal('restore');
        break;
    }
  }

  openModal(type: string) {
    let modalType;
    const width = '24';
    switch (type) {
      case 'archive':
        modalType = this.archiveModal();
        break;
      case 'restore':
        modalType = this.restoreModal();
        break;
    }
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: modalType,
      width: width,
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  archiveRequest() {
    this.isLoading.set(true);
    this.closeModal();
    this.createdTasksAdapter
      .archiveRequest(this.selectedWorkflowId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.checkCalledApi();
          this.getInsights();
          this.counterRefresh.emit('created_by_me');
        },
        error: (error: { error: { message: string } }) => {
          this.isLoading.set(false);
          this.myInboxService.handleResponseError(
            error,
            this.isLoading,
            this.toasterService
          );
        },
      });
  }

  restoreRequest() {
    this.isLoading.set(true);
    this.closeModal();
    this.createdTasksAdapter
      .restoreRequest(this.selectedWorkflowId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.checkCalledApi();
          this.getInsights();
          this.counterRefresh.emit('created_by_me');
        },
        error: (error: { error: { message: string } }) => {
          this.isLoading.set(false);
          this.myInboxService.handleResponseError(
            error,
            this.isLoading,
            this.toasterService
          );
        },
      });
  }

  onViewIconClick(row: CreatedRequest) {
    this.viewRequest(row.id);
  }

  submitExportForm(form: FormGroup) {
    const formValue = form.value;
    this.createdTasksAdapter
      .exportTasks(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => {
          this.exportService.handleExportSuccess(
            response as Response,
            form,
            () => this.closeModal()
          );
        },
        error: (error: { error?: { message?: string } }) => {
          this.exportService.handleExportError(
            error,
            () => this.closeModal(),
            this.toasterService
          );
          this.exportService.reInitExportForm(form);
        },
      });
  }
}
